import {Trans} from '@lingui/macro';
import {isNumber, keyBy, uniqBy} from 'lodash';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';

import {isDefined, OrNull} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisuallyHidden} from '../../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {emojiToDefinition} from '../../../../../srcCommon/helpers/emoji/emojiTextHelpers';
import {useDebounce, useUnmountCancelToken} from '../../../../../srcCommon/helpers/hookHelpers';
import {TeamAvatar} from '../../../../components/common/avatars/teamAvatar';
import {Emoji} from '../../../../components/common/emoji/emoji';
import {findTagNestingLevel, isPrivateTag} from '../../../../helpers/models/tagHelpers';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {HighlightTypesEnum} from '../../../../models/api/highlightBaseModel';
import {TagModel} from '../../../../models/api/tagModel';
import {TeamModel} from '../../../../models/api/teamModel';
import {baseEmojiValidationMap} from '../../../../services/emojis/emojis';
import {getCompanyUrl} from '../../../../store/data/company/companySelectors';
import {getFlatTeammates, getUser} from '../../../../store/data/teammates/teammatesSelectors';
import {getTeamsByNamespace} from '../../../../store/data/teams/teamsSelectors';
import {useBaseEditorContext} from '../../../editors/baseEditorContext';
import {filterTagExpressions} from '../../../editors/expressions/expressionInferHelpers';
import {SelectedDropdownItem} from '../dropdowns/dropdownWithExpressions';
import {MultipleChoiceDropdown} from '../dropdowns/multipleChoiceDropdown';
import {SingleChoiceDropdown} from '../dropdowns/singleChoiceDropdown';
import {useRuleEditorServiceContext} from '../hooks/useRuleEditorServiceContext';
import {
  defaultDropdownWidth,
  GenericMultipleParameterProps,
  GenericSingleParameterProps,
} from './genericParameter';

/*
 * Types.
 */

interface TagParameterProps extends GenericSingleParameterProps<TagModel> {}

interface TagsParameterProps extends GenericMultipleParameterProps<TagModel> {}

/*
 * Styles.
 */

const StyledNestingOffsetDiv = styled.div<{$nestingLevel: number}>`
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: 2px;
  justify-items: center;
  align-items: center;
  padding-left: ${(p) => Math.min(3, Math.max(p.$nestingLevel - 1, 0)) * 26}px;
`;

const StyledTagLabel = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-gap: 5px;
  align-items: center;
`;

const StyledArchivedBadge = styled.div`
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.veryTiny};
  color: ${(p) => p.theme.greys.shade80};
  text-transform: uppercase;
  padding: 3px 5px 3px;
  background-color: ${(p) => p.theme.alphas.gray20};
  border-radius: 100px;
  line-height: 1;
  margin-top: -2px;
`;

const StyledTeamIconDiv = styled.div`
  flex-shrink: 0;
  margin-left: 3px;
`;

/*
 * Component.
 */

export const TagParameter = (props: TagParameterProps) => {
  const [search, setSearch] = useState('');
  const selectedItem = useMemo(
    () => (props.selectedItem ? [props.selectedItem] : null),
    [props.selectedItem],
  );
  const tags = useTags(selectedItem, search);
  const tagExpressions = useMemo(() => filterTagExpressions(props.expressions, false), [props.expressions]);
  const debouncedSetSearch = useDebounce(setSearch, 200);
  const renderTagLabel = useRenderTagLabel(tags);

  return (
    <SingleChoiceDropdown
      {...props}
      expressions={tagExpressions}
      items={tags}
      getItemKey={(item) => String(item.id)}
      renderItemLabel={renderTagLabel}
      renderItemIcon={renderIcon(tags, search)}
      serializeItemForSearch={serializeItemForSearch}
      width={defaultDropdownWidth}
      onSearch={debouncedSetSearch}
      placeholder={<Trans id="rule-editor-select-tag">Select a tag</Trans>}
      title={props.onSelectCreateExpression ? <Trans id="rule-editor-tag-group">Tag</Trans> : null}
    />
  );
};

export const TagsParameter = (props: TagsParameterProps) => {
  const [search, setSearch] = useState('');
  const tags = useTags(props.selectedItems, search);
  const tagExpressions = useMemo(() => filterTagExpressions(props.expressions, true), [props.expressions]);
  const debouncedSetSearch = useDebounce(setSearch, 200);

  const renderTagLabel = useRenderTagLabel(tags);

  return (
    <MultipleChoiceDropdown<TagModel>
      {...props}
      expressions={tagExpressions}
      items={tags}
      getItemKey={(item) => String(item.id)}
      renderItemLabel={renderTagLabel}
      renderItemIcon={renderIcon(tags, search)}
      serializeItemForSearch={serializeItemForSearch}
      placeholder={<Trans id="rule-editor-select-tag">Select a tag</Trans>}
      anyLabel={<Trans id="rule-editor-any-tag">Any tag</Trans>}
      anyIcon={<Icon name="tag" />}
      width={defaultDropdownWidth}
      onSearch={debouncedSetSearch}
      title={props.onSelectCreateExpression ? <Trans id="rule-editor-tags-group">Tags</Trans> : null}
    />
  );
};

/*
 * Helpers.
 */

function serializeItemForSearch(item: TagModel) {
  return item.name;
}

function getIds(items: OrNull<ReadonlyArray<SelectedDropdownItem<TagModel>>>) {
  return items?.filter((item): item is TagModel['id'] => isNumber(item)) || [];
}

function useTags(selectedItems: OrNull<ReadonlyArray<SelectedDropdownItem<TagModel>>>, search: string) {
  const [activeTags, setActiveTags] = useState<ReadonlyArray<TagModel>>([]);
  const [extraTags, setExtraTags] = useState<Map<TagModel['id'], TagModel>>(
    new Map<TagModel['id'], TagModel>(),
  );
  const [archivedIds, setArchivedIds] = useState<OrNull<ReadonlyArray<TagModel['id']>>>(null);
  const user = useAppSelector(getUser);
  const teammates = useAppSelector(getFlatTeammates);
  const companyUrl = useAppSelector(getCompanyUrl);
  const cancelToken = useUnmountCancelToken();
  const {namespace} = useBaseEditorContext();
  const {tagService} = useRuleEditorServiceContext();

  // fetch active tags according to search
  useEffect(() => {
    tagService
      .fetch({
        companyUrl,
        namespace,
        cancelToken,
        search,
        user,
        teammates,
      })
      .then(setActiveTags);
  }, [tagService, companyUrl, namespace, cancelToken, search, user, teammates]);

  // fetch and store all selected tags that are not present
  useEffect(() => {
    const unfetchedIds = getIds(selectedItems).filter((id) => !extraTags.has(id));
    if (unfetchedIds.length === 0) {
      return;
    }

    tagService.fetchByIds({companyUrl, ids: unfetchedIds, cancelToken}).then((fetchedTags) => {
      if (!archivedIds) {
        // lazy init since archived tags can only be present if already selected when the rule loads
        setArchivedIds(fetchedTags.filter((tag) => isDefined(tag.archivedAt)).map((tag) => tag.id));
      }
      setExtraTags(fetchedTags.reduce((extraMap, tag) => extraMap.set(tag.id, tag), new Map(extraTags)));
    });
  }, [selectedItems, tagService, companyUrl, cancelToken]);

  return useMemo(() => {
    // because of nesting, we don't want to extract active ids from their context
    const unarchivedInactiveIds = getIds(selectedItems)
      .filter((id) => !archivedIds?.some((i) => i === id))
      .filter((id) => !activeTags.some((tag) => tag.id === id));

    return uniqBy(
      [...(archivedIds || []), ...unarchivedInactiveIds]
        .map((id) => extraTags.get(id))
        .filter(isDefined)
        .concat(activeTags),
      'id',
    );
  }, [selectedItems, extraTags, activeTags, search]);
}

function useRenderTagLabel(tags: ReadonlyArray<TagModel>) {
  const teams = useAppSelector(getTeamsByNamespace);
  const duplicateTags = useMemo((): ReadonlySet<string> => {
    const allTags = new Set<string>();
    const duplicatedTags = new Set<string>();

    tags.forEach((tag) => {
      if (allTags.has(tag.alias)) {
        duplicatedTags.add(tag.alias);
      }

      allTags.add(tag.alias);
    });

    return duplicatedTags;
  }, [tags]);

  return useCallback(
    (tag: TagModel, isInsideValue?: boolean) => {
      if (isInsideValue) {
        return tag.name;
      }

      const team = teams[tag.namespace];

      return (
        <StyledTagLabel>
          {maybeRenderArchivedLabel(tag)}
          {tag.name}
          {maybeRenderTeamIcon(tag, team, duplicateTags.has(tag.alias))}
        </StyledTagLabel>
      );
    },
    [duplicateTags, teams],
  );
}

function renderIcon(tags: ReadonlyArray<TagModel>, search: string) {
  return (tag: TagModel, isInsideValue?: boolean) => {
    if (isInsideValue || search) {
      return renderTagIcon(tag);
    }

    return renderTagIconWithNesting(tags, tag);
  };
}

function renderTagIconWithNesting(tags: ReadonlyArray<TagModel>, tag: TagModel) {
  const nestingLevel = findTagNestingLevel(keyBy(tags, 'id'), tag);
  return (
    <StyledNestingOffsetDiv $nestingLevel={nestingLevel}>
      {nestingLevel ? <Icon name="arrowNested" /> : null}
      {renderTagIcon(tag)}
    </StyledNestingOffsetDiv>
  );
}

function renderTagIcon(tag: TagModel) {
  switch (tag.type) {
    case HighlightTypesEnum.EMOJI: {
      const definition = emojiToDefinition(tag.highlightEmoji, baseEmojiValidationMap);
      if (!definition) {
        return null;
      }
      return <Emoji {...definition} size={16} />;
    }

    case HighlightTypesEnum.COLOR:
      return (
        <UserThemeConsumer>
          {(theme) => <Icon name="tag" color={theme.palette[tag.highlightColor].shade40} />}
        </UserThemeConsumer>
      );

    case HighlightTypesEnum.NONE:
      return <Icon name="tag" color={PaletteColorsEnum.GREY} />;

    default:
      return null;
  }
}

function maybeRenderTeamIcon(tag: TagModel, team: TeamModel | undefined, isDuplicated: boolean) {
  if (isPrivateTag(tag)) {
    return <Icon name="lockFilledNewTiny" color={PaletteColorsEnum.GREY} />;
  }

  if (!isDuplicated || !team) {
    return null;
  }

  return (
    <TooltipCoordinator render={() => team.name}>
      <StyledTeamIconDiv>
        <VisuallyHidden>{team.name}</VisuallyHidden>
        <TeamAvatar team={team} size={VisualSizesEnum.SMALL} />
      </StyledTeamIconDiv>
    </TooltipCoordinator>
  );
}

function maybeRenderArchivedLabel(tag: TagModel) {
  if (tag.archivedAt) {
    return (
      <StyledArchivedBadge>
        <Trans id="rule-editor-tag-parameter-archived">Archived</Trans>
      </StyledArchivedBadge>
    );
  }

  return null;
}
